// Header search dialog (mockup: .search-dialog). Native <dialog> provides the focus trap, Escape and backdrop;
// <predictive-search> inside it renders live results that replace the popular artworks list.
if (!customElements.get('search-dialog')) {
  class SearchDialog extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;

      this.input = this.querySelector('input[type="search"]');
      this.browseLink = this.querySelector('[data-search-browse]');
      this.browseLabel = this.querySelector('[data-search-browse-label]');
      this.clearButton = this.querySelector('[data-search-clear]');
      this.opener = null;
      // Matches the mobile/tablet layout in component-search-dialog.css
      this.mobileQuery = window.matchMedia('(max-width: 989px)');
      this.onViewportChange = this.onViewportChange.bind(this);

      // Mobile/tablet results scroll indicator (the popular list or the live results scroll, one at a time)
      this.scrollers = [this.querySelector('.search-dialog__popular'), this.querySelector('[data-predictive-search]')].filter(
        Boolean
      );
      this.scrollTrack = this.querySelector('[data-search-scroll-track]');
      this.scrollThumb = this.querySelector('[data-search-scroll-thumb]');
      this.scrollFade = this.querySelector('[data-search-scroll-fade]');
      this.updateScrollIndicator = this.updateScrollIndicator.bind(this);
      this.requestScrollIndicator = this.requestScrollIndicator.bind(this);

      this.onDocumentClick = (event) => {
        const opener = event.target.closest?.('[data-search-dialog-open]');
        if (!opener) return;
        event.preventDefault();
        this.open(opener);
      };
      document.addEventListener('click', this.onDocumentClick);

      // "/" opens search, as in the mockup.
      this.onDocumentKeydown = (event) => {
        if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return;
        if (event.target?.closest?.('input, textarea, select, [contenteditable="true"]')) return;
        if (document.querySelector('dialog[open]')) return;
        event.preventDefault();
        this.open(document.activeElement);
      };
      document.addEventListener('keydown', this.onDocumentKeydown);

      this.querySelectorAll('[data-search-dialog-close]').forEach((button) =>
        button.addEventListener('click', () => this.close())
      );

      // Clicks on the backdrop land on the dialog element itself, outside its box.
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        const inside =
          event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
        if (!inside) this.close();
      });

      this.dialog.addEventListener('close', () => {
        this.stopScrollIndicator();
        this.stopViewportSizing();
        this.unlockScroll();
        document.body.classList.remove('overflow-hidden');
        document.querySelectorAll('[data-search-dialog-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
        if (this.opener?.isConnected) this.opener.focus();
      });

      this.querySelectorAll('[data-search-term]').forEach((chip) =>
        chip.addEventListener('click', () => {
          if (!this.input) return;
          this.input.value = chip.dataset.searchTerm;
          this.input.dispatchEvent(new Event('input', { bubbles: true }));
          this.input.focus();
        })
      );

      // Keep focus (and the on-screen keyboard) in the field while the clear button is tapped
      this.clearButton?.addEventListener('pointerdown', (event) => event.preventDefault());
      this.clearButton?.addEventListener('click', () => this.clear());

      this.input?.addEventListener('input', () => {
        this.updateBrowseLink();
        this.updateClearButton();
      });
      this.updateBrowseLink();
      this.updateClearButton();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      this.stopScrollIndicator();
      this.stopViewportSizing();
    }

    open(opener) {
      if (this.dialog.open) return;
      document.querySelector('mega-nav')?.close?.();
      this.opener = opener || document.activeElement;
      if (this.mobileQuery.matches) {
        this.lockScroll();
        this.startViewportSizing();
      }
      this.dialog.showModal();
      document.body.classList.add('overflow-hidden');
      if (this.mobileQuery.matches) this.startScrollIndicator();
      document.querySelectorAll('[data-search-dialog-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
      setTimeout(() => this.input?.focus(), 30);
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    // Clearing goes through the form's reset, which predictive-search.js handles: it empties the field,
    // cancels pending requests and brings back the default list.
    clear() {
      if (!this.input) return;
      this.input.form.reset();
      this.input.value = '';
      this.input.focus();
      this.updateBrowseLink();
      this.updateClearButton();
    }

    updateClearButton() {
      if (this.clearButton) this.clearButton.hidden = !this.input?.value;
    }

    // Size the dialog to the visible area above the on-screen keyboard. iOS Safari and Android Chrome
    // shrink the visual viewport (not the layout viewport) when the keyboard opens; without
    // visualViewport the CSS falls back to 100dvh / 100vh.
    startViewportSizing() {
      const viewport = window.visualViewport;
      if (!viewport || this.sizingViewport) return;
      this.sizingViewport = true;
      viewport.addEventListener('resize', this.onViewportChange);
      viewport.addEventListener('scroll', this.onViewportChange);
      this.onViewportChange();
    }

    stopViewportSizing() {
      const viewport = window.visualViewport;
      if (!viewport || !this.sizingViewport) return;
      this.sizingViewport = false;
      viewport.removeEventListener('resize', this.onViewportChange);
      viewport.removeEventListener('scroll', this.onViewportChange);
      this.dialog.style.removeProperty('--search-vvh');
      this.dialog.style.removeProperty('--search-vv-top');
    }

    onViewportChange() {
      const viewport = window.visualViewport;
      this.dialog.style.setProperty('--search-vvh', `${viewport.height}px`);
      this.dialog.style.setProperty('--search-vv-top', `${viewport.offsetTop}px`);
      // The list gets shorter/taller when the keyboard opens or closes
      this.requestScrollIndicator();
    }

    // Updates on list scroll, list resize (dialog/keyboard size changes) and when live results are swapped in.
    startScrollIndicator() {
      if (!this.scrollTrack || this.indicatorRunning) return;
      this.indicatorRunning = true;
      this.scrollers.forEach((scroller) =>
        scroller.addEventListener('scroll', this.requestScrollIndicator, { passive: true })
      );
      if (typeof ResizeObserver !== 'undefined') {
        this.indicatorResizeObserver = new ResizeObserver(this.requestScrollIndicator);
        this.scrollers.forEach((scroller) => this.indicatorResizeObserver.observe(scroller));
      }
      this.indicatorMutationObserver = new MutationObserver(this.requestScrollIndicator);
      const searchBody = this.querySelector('.search-dialog__body');
      if (searchBody) {
        this.indicatorMutationObserver.observe(searchBody, {
          attributes: true,
          attributeFilter: ['results', 'loading', 'open'],
        });
      }
      this.scrollers.forEach((scroller) =>
        this.indicatorMutationObserver.observe(scroller, { childList: true, subtree: true })
      );
      this.requestScrollIndicator();
    }

    stopScrollIndicator() {
      if (!this.indicatorRunning) return;
      this.indicatorRunning = false;
      this.scrollers.forEach((scroller) => scroller.removeEventListener('scroll', this.requestScrollIndicator));
      this.indicatorResizeObserver?.disconnect();
      this.indicatorMutationObserver?.disconnect();
      window.cancelAnimationFrame(this.indicatorFrame);
      this.indicatorFrame = null;
      if (this.scrollTrack) this.scrollTrack.hidden = true;
      if (this.scrollFade) this.scrollFade.hidden = true;
    }

    requestScrollIndicator() {
      if (!this.indicatorRunning || this.indicatorFrame) return;
      this.indicatorFrame = window.requestAnimationFrame(() => {
        this.indicatorFrame = null;
        this.updateScrollIndicator();
      });
    }

    // Thumb height = visible height / total height of the list; thumb position = scroll ratio.
    updateScrollIndicator() {
      const scroller = this.scrollers.find((element) => element.offsetParent !== null);
      const overflow = scroller ? scroller.scrollHeight - scroller.clientHeight : 0;
      if (!scroller || overflow <= 1) {
        this.scrollTrack.hidden = true;
        if (this.scrollFade) this.scrollFade.hidden = true;
        return;
      }

      const { clientHeight, scrollHeight, scrollTop, offsetTop } = scroller;
      const thumbHeight = Math.max(24, (clientHeight / scrollHeight) * clientHeight);
      const ratio = Math.min(1, Math.max(0, scrollTop / overflow));

      this.scrollTrack.hidden = false;
      this.scrollTrack.style.top = `${offsetTop}px`;
      this.scrollTrack.style.height = `${clientHeight}px`;
      this.scrollThumb.style.height = `${thumbHeight}px`;
      this.scrollThumb.style.transform = `translateY(${(clientHeight - thumbHeight) * ratio}px)`;

      if (this.scrollFade) {
        const atBottom = scrollTop >= overflow - 1;
        this.scrollFade.hidden = atBottom;
        if (!atBottom) this.scrollFade.style.top = `${offsetTop + clientHeight - this.scrollFade.offsetHeight}px`;
      }
    }

    // Pin the page in place (iOS ignores overflow: hidden on body) and restore the exact scroll position on close
    // While locked and while the position is restored, <body data-scroll-locked> tells the sticky header
    // (sections/header.liquid isLocked) to ignore those scroll jumps, so it looks the same after closing.
    lockScroll() {
      if (this.scrollLocked) return;
      this.scrollLocked = true;
      window.clearTimeout(this.scrollUnlockTimer);
      document.body.setAttribute('data-scroll-locked', '');
      this.lockedScrollY = window.scrollY;
      Object.assign(document.body.style, {
        position: 'fixed',
        top: `-${this.lockedScrollY}px`,
        left: '0',
        right: '0',
        width: '100%',
      });
    }

    unlockScroll() {
      if (!this.scrollLocked) return;
      this.scrollLocked = false;
      Object.assign(document.body.style, { position: '', top: '', left: '', right: '', width: '' });
      const root = document.documentElement;
      const scrollBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollTo(0, this.lockedScrollY);
      root.style.scrollBehavior = scrollBehavior;
      // The restore's scroll event is handled on a later frame; release the header after it.
      this.scrollUnlockTimer = window.setTimeout(() => document.body.removeAttribute('data-scroll-locked'), 150);
    }

    updateBrowseLink() {
      if (!this.browseLink || !this.browseLabel) return;
      const terms = this.input?.value.trim() || '';
      const { defaultHref, defaultLabel, queryLabel } = this.browseLink.dataset;

      if (!terms) {
        this.browseLink.href = defaultHref;
        this.browseLabel.textContent = defaultLabel;
        return;
      }

      const params = new URLSearchParams({ q: terms, 'options[prefix]': 'last' });
      this.browseLink.href = `${this.input.form.getAttribute('action')}?${params}`;
      this.browseLabel.textContent = queryLabel.replace('[terms]', terms);
    }
  }

  customElements.define('search-dialog', SearchDialog);
}
